import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import {
  api,
  ApiError,
  AppSettings,
  DhisAppClient,
  ServiceProfile,
  SecretStatus,
  TenantWorkspace,
  loadSession,
  isPlatformAdmin
} from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

const EMPTY_PROFILE: ServiceProfile = {
  serviceName: "dhis2",
  environment: "",
  baseUrl: "",
  apiVersion: "",
  healthEndpoint: "/api/system/info",
  authMode: "token",
  requestTimeoutMs: 15000,
  retryCount: 2,
  enabled: true,
  notes: ""
};

type EditorMode = { kind: "closed" } | { kind: "create" } | { kind: "edit"; original: string };
type FlashKind = "ok" | "error" | "info";
type Flash = { kind: FlashKind; text: string } | null;

type DiscoveryResult = {
  app: { key: string; name: string; version?: string; launchUrl?: string };
  client: DhisAppClient;
  clientSecret: string | null;
  created: boolean;
  baseUrl: string;
  message?: string;
};

function pillForSecret(profile: ServiceProfile) {
  if (!profile.secretConfigured) return <span className="pill pill-warn">No secret</span>;
  return <span className="pill pill-ok">Secret vaulted</span>;
}

function pillForAuthMode(mode: ServiceProfile["authMode"]) {
  const label = mode.replace("-", " ");
  if (mode === "none") return <span className="pill pill-muted">{label}</span>;
  return <span className="pill pill-info">{label}</span>;
}

function formatDate(value?: string): string {
  if (!value) return "—";
  try { return new Date(value).toLocaleString(); } catch { return value; }
}

function needsCredentials(mode: ServiceProfile["authMode"]): boolean {
  return mode !== "none";
}

function secretKindForAuthMode(mode: ServiceProfile["authMode"]): SecretStatus["kind"] {
  if (mode === "basic" || mode === "dhis2-session") return "dhis2-basic";
  return "dhis2-pat";
}

export default function DhisInstances() {
  const session = loadSession();
  const isAdmin = session?.role === "admin";
  // Platform admins author profiles on behalf of any workspace, so the
  // form needs a workspace selector. Workspace admins implicitly create
  // in their own workspace and never see the selector.
  const isPlatformAdminUser = isPlatformAdmin(session);

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash>(null);

  // Workspace catalogue for the platform-admin workspace selector. Loaded
  // lazily via api.workspace() (which returns the full list when called
  // by a platform operator). Empty for workspace admins.
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);

  const [editor, setEditor] = useState<EditorMode>({ kind: "closed" });
  const [form, setForm] = useState<ServiceProfile>(EMPTY_PROFILE);
  const [secretModal, setSecretModal] = useState<{ profile: ServiceProfile; rotate: boolean } | null>(null);
  const [secretToken, setSecretToken] = useState("");
  const [secretUsername, setSecretUsername] = useState("");
  const [secretPassword, setSecretPassword] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [discovery, setDiscovery] = useState<DiscoveryResult | null>(null);

  const profiles = useMemo(() => settings?.serviceProfiles ?? [], [settings]);

  const tProfiles = useTableState(profiles as unknown as Array<Record<string, unknown>>, {
    searchFields: ["environment", "serviceName", "baseUrl", "apiVersion", "authMode", "notes"],
    initialPageSize: 25
  });

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await api.settings();
      setSettings(data);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message}${err.code ? ` (${err.code})` : ""}` : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!isPlatformAdminUser) return;
    api.workspace()
      .then((r) => setWorkspaces(r.workspaces ?? []))
      .catch(() => {/* selector falls back to free-text input */});
  }, [isPlatformAdminUser]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  const openCreate = () => {
    setForm(EMPTY_PROFILE);
    setEditor({ kind: "create" });
  };

  const openEdit = (profile: ServiceProfile) => {
    setForm({ ...profile });
    setEditor({ kind: "edit", original: profile.environment });
  };

  const closeEditor = () => {
    setEditor({ kind: "closed" });
    setForm(EMPTY_PROFILE);
  };

  const setField = <K extends keyof ServiceProfile>(key: K, value: ServiceProfile[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (isPlatformAdminUser && editor.kind === "create" && !form.workspaceId) {
      setFlash({ kind: "error", text: "Select a target workspace for this instance." });
      return;
    }
    setBusy("save");
    setFlash(null);
    try {
      const payload: ServiceProfile = {
        ...form,
        environment: form.environment.trim(),
        baseUrl: form.baseUrl.trim(),
        apiVersion: form.apiVersion.trim(),
        healthEndpoint: form.healthEndpoint.trim() || "/api/system/info"
      };
      const next = editor.kind === "create"
        ? await api.createServiceProfile(payload)
        : await api.updateServiceProfile((editor as { original: string }).original, payload);
      setSettings(next);
      setFlash({ kind: "ok", text: `Instance "${payload.environment}" saved. Add a credential next — the API version will be auto-detected.` });
      closeEditor();
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (env: string) => {
    if (!confirm(`Remove DHIS2 instance "${env}"? Any stored secret will be revoked.`)) return;
    setBusy(`del:${env}`);
    setFlash(null);
    try {
      const next = await api.deleteServiceProfile(env);
      setSettings(next);
      setFlash({ kind: "ok", text: `Instance "${env}" removed.` });
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onVerify = async (env: string) => {
    setBusy(`verify:${env}`);
    setFlash(null);
    try {
      const result = await api.verifyServiceProfileSecret(env);
      if (result.ok && result.authenticated) {
        const detail = result.version ? ` · DHIS2 ${result.version}${result.systemName ? ` (${result.systemName})` : ""}` : "";
        setFlash({
          kind: "ok",
          text: `Secret for "${env}" verified (${result.kind}, rotated ${formatDate(result.rotatedAt)})${detail}.`
        });
      } else if (result.reachable && !result.authenticated) {
        setFlash({
          kind: "error",
          text: `"${env}" is reachable but the stored credential was rejected. ${result.message ?? "Rotate the secret and try again."}`
        });
      } else {
        setFlash({
          kind: "error",
          text: `Verification failed for "${env}". ${result.message ?? ""}`.trim()
        });
      }
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onRevokeSecret = async (env: string) => {
    if (!confirm(`Revoke stored secret for "${env}"?`)) return;
    setBusy(`revoke:${env}`);
    setFlash(null);
    try {
      const next = await api.revokeServiceProfileSecret(env);
      setSettings(next);
      setFlash({ kind: "ok", text: `Secret revoked for "${env}".` });
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const openSecretModal = (profile: ServiceProfile, rotate: boolean) => {
    setSecretToken("");
    setSecretUsername("");
    setSecretPassword("");
    setSecretModal({ profile, rotate });
  };

  const submitSecret = async (e: FormEvent) => {
    e.preventDefault();
    if (!secretModal) return;
    const { profile, rotate } = secretModal;
    let value = "";
    const kind = secretKindForAuthMode(profile.authMode);
    if (profile.authMode === "token") {
      value = secretToken.trim();
      if (!value) {
        setFlash({ kind: "error", text: "Personal access token is required." });
        return;
      }
    } else if (profile.authMode === "basic" || profile.authMode === "dhis2-session") {
      const u = secretUsername.trim();
      const p = secretPassword;
      if (!u || !p) {
        setFlash({ kind: "error", text: "Username and password are required." });
        return;
      }
      value = `${u}:${p}`;
    } else {
      setFlash({ kind: "error", text: "This auth mode does not use a credential." });
      return;
    }
    setBusy(`secret:${profile.environment}`);
    setFlash(null);
    try {
      if (rotate) {
        const result = await api.rotateServiceProfileSecret(profile.environment, value, kind);
        setSettings(result.settings);
        setFlash({ kind: "ok", text: `Secret rotated for "${profile.environment}" at ${formatDate(result.rotatedAt)}.` });
      } else {
        const next = await api.setServiceProfileSecret(profile.environment, value, kind);
        setSettings(next);
        setFlash({ kind: "ok", text: `Secret stored for "${profile.environment}".` });
      }
      setSecretModal(null);
      setSecretToken("");
      setSecretUsername("");
      setSecretPassword("");
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onSetActive = async (env: string) => {
    if (!settings) return;
    setBusy(`active:${env}`);
    setFlash(null);
    try {
      const next = await api.updateSettings({ ...settings, activeServiceProfile: env });
      setSettings(next);
      setFlash({ kind: "ok", text: `"${env}" is now the active DHIS2 instance.` });
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const onDiscoverAppClient = async (profile: ServiceProfile) => {
    setBusy(`discover:${profile.environment}`);
    setFlash(null);
    try {
      const result = await api.discoverServiceProfileAppClient(profile.environment);
      setDiscovery({ ...result, baseUrl: profile.baseUrl });
      if (result.created) {
        setFlash({ kind: "ok", text: `Discovered TallyStream app on "${profile.environment}" and issued new credentials.` });
      } else {
        setFlash({ kind: "info", text: `TallyStream app is already provisioned for "${profile.environment}".` });
      }
    } catch (err) {
      setFlash({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlash({ kind: "ok", text: "Copied to clipboard." });
    } catch {
      setFlash({ kind: "error", text: "Copy failed. Select the value and press Ctrl/Cmd-C." });
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>DHIS2 instances</h1>
          <p>Register the DHIS2 servers TallyStream connects to. Credentials are encrypted and stored server-side. App client credentials are auto-provisioned by discovering the TallyStream app on the DHIS2 server.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
          {isAdmin && <button className="btn btn-primary" onClick={openCreate}>Add DHIS2 instance</button>}
        </div>
      </header>

      {!isAdmin && (
        <div className="alert alert-info">
          You are signed in as <strong>{session?.role ?? "guest"}</strong>. Only administrators can create or modify DHIS2 instance configurations.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {flash && <div className={`alert alert-${flash.kind === "ok" ? "ok" : flash.kind === "error" ? "error" : "info"}`}>{flash.text}</div>}

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : profiles.length === 0 ? (
        <div className="empty">
          No DHIS2 instances configured yet.
          {isAdmin && <> Click <strong>Add DHIS2 instance</strong> to register one.</>}
        </div>
      ) : (
        <div className="table-scroll">
          <TableToolbar
            search={tProfiles.search} setSearch={tProfiles.setSearch}
            page={tProfiles.page} setPage={tProfiles.setPage}
            pageSize={tProfiles.pageSize} setPageSize={tProfiles.setPageSize}
            totalPages={tProfiles.totalPages} totalRows={tProfiles.totalRows}
            hideExport
            searchPlaceholder="Search instances…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Environment</th>
                <th>Base URL</th>
                <th>API</th>
                <th>Auth</th>
                <th>Secret</th>
                <th>State</th>
                <th>Last ok</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(tProfiles.paged as unknown as ServiceProfile[]).map((p) => {
                const active = settings?.activeServiceProfile === p.environment;
                return (
                  <tr key={p.environment}>
                    <td>
                      <strong>{p.environment}</strong>
                      {active && <span className="pill pill-ok" style={{ marginLeft: 8 }}>Active</span>}
                      {p.notes && <div className="soft" style={{ marginTop: 4, fontSize: 12 }}>{p.notes}</div>}
                    </td>
                    <td className="truncate" title={p.baseUrl}>
                      <a href={p.baseUrl} target="_blank" rel="noreferrer">{p.baseUrl}</a>
                    </td>
                    <td>{p.apiVersion ? <code>{p.apiVersion}</code> : <span className="soft">auto</span>}</td>
                    <td>{pillForAuthMode(p.authMode)}</td>
                    <td>{pillForSecret(p)}</td>
                    <td>
                      {p.enabled ? <span className="pill pill-ok">Enabled</span> : <span className="pill pill-muted">Disabled</span>}
                    </td>
                    <td className="soft">{formatDate(p.lastSuccessfulConnection)}</td>
                    <td className="actions">
                      {isAdmin && (
                        <div className="btn-row" style={{ justifyContent: "flex-end" }}>
                          {!active && (
                            <button
                              className="btn btn-sm"
                              disabled={busy === `active:${p.environment}`}
                              onClick={() => void onSetActive(p.environment)}
                            >Set active</button>
                          )}
                          <button className="btn btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          {needsCredentials(p.authMode) && (p.secretConfigured ? (
                            <>
                              <button
                                className="btn btn-sm"
                                disabled={busy === `verify:${p.environment}`}
                                onClick={() => void onVerify(p.environment)}
                              >Verify</button>
                              <button
                                className="btn btn-sm"
                                onClick={() => openSecretModal(p, true)}
                              >Rotate</button>
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={busy === `discover:${p.environment}`}
                                onClick={() => void onDiscoverAppClient(p)}
                                title="Detect the TallyStream app installed on this DHIS2 server and issue client credentials."
                              >
                                {busy === `discover:${p.environment}` ? "Detecting…" : "Discover app"}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                disabled={busy === `revoke:${p.environment}`}
                                onClick={() => void onRevokeSecret(p.environment)}
                              >Revoke</button>
                            </>
                          ) : (
                            <button className="btn btn-sm btn-primary" onClick={() => openSecretModal(p, false)}>
                              Add secret
                            </button>
                          ))}
                          <button
                            className="btn btn-sm btn-danger"
                            disabled={busy === `del:${p.environment}`}
                            onClick={() => void onDelete(p.environment)}
                          >Remove</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor.kind !== "closed" && (
        <ProfileEditor
          title={editor.kind === "create" ? "Add DHIS2 instance" : `Edit: ${(editor as { original: string }).original}`}
          form={form}
          setField={setField}
          onSubmit={submitForm}
          onClose={closeEditor}
          submitting={busy === "save"}
          isPlatformAdmin={isPlatformAdminUser}
          workspaces={workspaces}
        />
      )}

      {secretModal && (
        <SecretEditor
          profile={secretModal.profile}
          rotate={secretModal.rotate}
          token={secretToken}
          username={secretUsername}
          password={secretPassword}
          onChangeToken={setSecretToken}
          onChangeUsername={setSecretUsername}
          onChangePassword={setSecretPassword}
          onSubmit={submitSecret}
          onClose={() => setSecretModal(null)}
          submitting={busy === `secret:${secretModal.profile.environment}`}
        />
      )}

      {discovery && (
        <DiscoveryDialog
          result={discovery}
          onClose={() => setDiscovery(null)}
          onCopy={copyToClipboard}
        />
      )}
    </div>
  );
}

function ProfileEditor({
  title, form, setField, onSubmit, onClose, submitting, isPlatformAdmin: isPa, workspaces
}: {
  title: string;
  form: ServiceProfile;
  setField: <K extends keyof ServiceProfile>(key: K, value: ServiceProfile[K]) => void;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
  submitting: boolean;
  isPlatformAdmin: boolean;
  workspaces: TenantWorkspace[];
}) {
  return (
    <div style={overlayStyle}>
      <form className="card" style={modalStyle} onSubmit={onSubmit}>
        <div className="card-head">
          <h2>{title}</h2>
          <button type="button" className="linkbtn" onClick={onClose}>Close</button>
        </div>
        <div className="card-body">
          <div className="form-grid">
            {/* Workspace selector — platform-admin only. The backend will
                stamp workspaceId from the body for platform operators;
                workspace admins implicitly use their own and never see
                this field. */}
            {isPa && (
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label>Target workspace <span style={{ color: "var(--err)" }}>*</span></label>
                <select
                  value={form.workspaceId ?? ""}
                  onChange={(e) => setField("workspaceId", e.target.value || undefined)}
                  required
                >
                  <option value="">Select a workspace…</option>
                  {workspaces.map((w) => {
                    const org = (w as { organizationName?: string }).organizationName;
                    const wsName = (w as { workspaceName?: string }).workspaceName;
                    const display = [org, wsName].filter(Boolean).join(" · ") || w.name || w.id;
                    return (
                      <option key={w.id} value={w.id}>
                        {display} ({w.slug ?? w.id})
                      </option>
                    );
                  })}
                </select>
                <small>Which tenant this instance is provisioned for. Cannot be changed after creation.</small>
              </div>
            )}
            <div className="field">
              <label>Environment <span style={{ color: "var(--err)" }}>*</span></label>
              <input value={form.environment} onChange={(e) => setField("environment", e.target.value)} required placeholder="e.g. production" />
              <small>Unique identifier used to reference this instance.</small>
            </div>
            <div className="field">
              <label>Service name</label>
              <input value={form.serviceName} onChange={(e) => setField("serviceName", e.target.value)} required />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Base URL <span style={{ color: "var(--err)" }}>*</span></label>
              <input
                type="url"
                value={form.baseUrl}
                onChange={(e) => setField("baseUrl", e.target.value)}
                required
                placeholder="https://dhis2.example.org"
              />
              <small>
                The API version is auto-detected from <code>/api/system/info</code> after you store a credential.
                {form.apiVersion && <> Currently detected: <code>{form.apiVersion}</code>.</>}
              </small>
            </div>
            <div className="field">
              <label>Health endpoint</label>
              <input value={form.healthEndpoint} onChange={(e) => setField("healthEndpoint", e.target.value)} required />
            </div>
            <div className="field">
              <label>Auth mode</label>
              <select value={form.authMode} onChange={(e) => setField("authMode", e.target.value as ServiceProfile["authMode"])}>
                <option value="token">Personal access token</option>
                <option value="basic">Basic auth (username + password)</option>
                <option value="dhis2-session">DHIS2 session (username + password)</option>
                <option value="none">None</option>
              </select>
              <small>The credential form adapts to the selected method.</small>
            </div>
            <div className="field">
              <label>Request timeout (ms)</label>
              <input
                type="number" min={1000} step={500}
                value={form.requestTimeoutMs}
                onChange={(e) => setField("requestTimeoutMs", Math.max(1000, Number(e.target.value) || 0))}
              />
            </div>
            <div className="field">
              <label>Retry count</label>
              <input
                type="number" min={0} max={10}
                value={form.retryCount}
                onChange={(e) => setField("retryCount", Math.max(0, Number(e.target.value) || 0))}
              />
            </div>
            <div className="field">
              <label>Enabled</label>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={form.enabled} onChange={(e) => setField("enabled", e.target.checked)} />
                <span>{form.enabled ? "Accepting traffic" : "Quarantined"}</span>
              </label>
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Operational notes, owner, DR posture…" />
            </div>
          </div>
          <div className="alert alert-info">
            Credentials are stored separately. Save the instance first, then use <strong>Add secret</strong> to provide the {form.authMode === "token" ? "personal access token" : form.authMode === "none" ? "(no credential required)" : "username + password"}.
          </div>
        </div>
        <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : "Save instance"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SecretEditor({
  profile, rotate, token, username, password,
  onChangeToken, onChangeUsername, onChangePassword,
  onSubmit, onClose, submitting
}: {
  profile: ServiceProfile;
  rotate: boolean;
  token: string;
  username: string;
  password: string;
  onChangeToken: (v: string) => void;
  onChangeUsername: (v: string) => void;
  onChangePassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
  submitting: boolean;
}) {
  const isBasic = profile.authMode === "basic" || profile.authMode === "dhis2-session";
  const isToken = profile.authMode === "token";
  const env = profile.environment;
  const headline = rotate ? `Rotate secret — ${env}` : `Add secret — ${env}`;
  return (
    <div style={overlayStyle}>
      <form className="card" style={{ ...modalStyle, maxWidth: 560 }} onSubmit={onSubmit}>
        <div className="card-head">
          <h2>{headline}</h2>
          <button type="button" className="linkbtn" onClick={onClose}>Close</button>
        </div>
        <div className="card-body">
          <div className="alert alert-info" style={{ marginBottom: 12 }}>
            Auth mode for this instance: <strong>{profile.authMode.replace("-", " ")}</strong>.
          </div>
          {isToken && (
            <div className="field">
              <label>DHIS2 personal access token</label>
              <input
                type="password"
                autoComplete="new-password"
                value={token}
                onChange={(e) => onChangeToken(e.target.value)}
                required
                placeholder="Paste the token generated in DHIS2 → My profile"
              />
              <small>The value is encrypted with AES-256-GCM server-side and never echoed back.</small>
            </div>
          )}
          {isBasic && (
            <>
              <div className="field">
                <label>DHIS2 username</label>
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => onChangeUsername(e.target.value)}
                  required
                  placeholder="e.g. tallystream.svc"
                />
              </div>
              <div className="field">
                <label>DHIS2 password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => onChangePassword(e.target.value)}
                  required
                />
                <small>Stored as an encrypted <code>username:password</code> pair — AES-256-GCM.</small>
              </div>
            </>
          )}
          <div className="alert alert-warn">
            {rotate
              ? "Rotating invalidates the previous credential immediately. Background syncs using the old secret will fail until they are restarted."
              : "TallyStream servers will use this credential for all DHIS2 requests on this environment."}
          </div>
        </div>
        <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : rotate ? "Rotate secret" : "Store secret"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DiscoveryDialog({
  result, onClose, onCopy
}: {
  result: DiscoveryResult;
  onClose: () => void;
  onCopy: (text: string) => void;
}) {
  return (
    <div style={overlayStyle}>
      <div className="card" style={{ ...modalStyle, maxWidth: 600 }}>
        <div className="card-head">
          <h2>{result.created ? "TallyStream app discovered" : "TallyStream app already linked"}</h2>
          <button type="button" className="linkbtn" onClick={onClose}>Close</button>
        </div>
        <div className="card-body">
          {result.created && result.clientSecret ? (
            <div className="alert alert-warn">
              The client secret below is shown <strong>only once</strong>. Paste it into the TallyStream app in DHIS2 → Apps → TallyStream → Setup before closing this dialog.
            </div>
          ) : (
            <div className="alert alert-info">
              {result.message ?? "A client is already provisioned for this DHIS2 instance. Use the DHIS2 app clients page to rotate its secret."}
            </div>
          )}
          <dl className="dl-kv">
            <dt>Installed app</dt>
            <dd>
              <strong>{result.app.name}</strong>
              {result.app.version && <span className="soft"> · v{result.app.version}</span>}
              <div className="soft" style={{ fontSize: 12 }}><code>{result.app.key}</code></div>
            </dd>
            <dt>DHIS2 base URL</dt>
            <dd><code>{result.baseUrl}</code></dd>
            <dt>Client ID</dt>
            <dd>
              <code>{result.client.clientId}</code>{" "}
              <button className="btn btn-sm" type="button" onClick={() => onCopy(result.client.clientId)}>Copy</button>
            </dd>
            {result.created && result.clientSecret && (
              <>
                <dt>Client secret</dt>
                <dd>
                  <code style={{ wordBreak: "break-all" }}>{result.clientSecret}</code>{" "}
                  <button className="btn btn-sm" type="button" onClick={() => onCopy(result.clientSecret!)}>Copy</button>
                </dd>
              </>
            )}
            <dt>Workspace</dt>
            <dd><code>{result.client.workspaceId}</code></dd>
          </dl>
        </div>
        <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {result.created ? "I've copied the secret" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(33,41,52,0.5)",
  display: "grid", placeItems: "center", padding: 16, zIndex: 100
};
const modalStyle: React.CSSProperties = {
  width: "min(720px, 100%)", maxHeight: "90vh", overflow: "auto",
  background: "var(--surface)", border: "1px solid var(--border)"
};
